import React, {useContext, useMemo, useState} from 'react'
import classNames from 'classnames'
import useTranslate from 'modules/local/useTranslate'
import {
    IoCheckmarkCircleOutline,
    IoInformationCircleOutline,
    IoLockClosedOutline,
    IoPaperPlaneOutline,
    IoPersonOutline,
    IoSyncOutline,
} from 'react-icons/io5'
import WorkspaceContext from '../WorkspaceContext'
import WorkspaceParishOverviewSection from '../Insights/WorkspaceParishOverviewSection'
import {AccessPortalIntroBlock} from '../AccessPortal/AccessPortalIntroBlock'
// Danh sách 6 bí tích + nhãn dịch sẵn - LẤY LẠI NGUYÊN từ MemberDetailModal.js (1 nguồn duy nhất,
// đúng convention đã dùng ở FamilyBookImportExportPage.js) để dải chip ở khối "Khóa Bí Tích" luôn
// khớp ĐÚNG 6 loại thật của hệ thống, không gõ tay tên bí tích rồi lệch/thiếu (bug thực tế đã gặp ở
// bản nháp: liệt kê thiếu Xức Dầu Bệnh Nhân + gọi nhầm "Thánh Thể" thay vì đúng tên "Rước Lễ Lần
// Đầu" dùng xuyên suốt app). KHÔNG import từ WorkspaceFamilyBook.js dù nơi đó cũng có export cùng
// tên - file đó đã import ngược lại FamilyBookOverviewPage.js, tạo vòng lặp.
import {ALL_SACRAMENT_TYPES, SACRAMENT_LABELS} from '../../AppLocals/FamilyBook/components/MemberDetailModal'

// Trang "Tổng Quan" - mục ĐẦU TIÊN trong nhóm menu Sổ sách & Bí tích (xem FAMILY_BOOK_SUB_ITEMS,
// Workspace.js) - KHÔNG phải màn thao tác nghiệp vụ, chỉ tổng hợp: (1) thống kê ĐÃ CÓ SẴN (tái
// dùng NGUYÊN WorkspaceParishOverviewSection - đúng dữ liệu đang hiện ở Tổng quan Workspace chung,
// không tạo API/tính toán riêng), (2) sơ đồ + giải thích quyền/nghĩa vụ khi gắn sổ vào giáo xứ, (3)
// lối vào nhanh "/access-portal" để admin copy link chia sẻ cho người được cấp quyền tạm thời (xem
// AccessPortalIntroBlock.js - dùng chung với Kristo Education/organization_manage_course, và
// WorkspaceFeatureAccessGrants.js - nơi THẬT SỰ quản lý danh sách người được cấp).

// ─── Sơ đồ 5 giai đoạn (bấm vào 1 khối -> cập nhật panel giải thích bên dưới) ──

// Mỗi khối = 1 giai đoạn vòng đời sổ, tự đứng riêng (không còn dùng chung màu family-theme) để phân
// biệt trực quan nhanh - toàn bộ class Tailwind viết TĨNH (không nội suy `text-${color}-600`) để
// JIT purge chắc chắn giữ lại, xem bài học ở bản trước.
const BLOCK_THEMES = {
    blue: {
        badge: 'bg-blue-600', tag: 'bg-blue-50 text-blue-700', title: 'text-blue-700',
        border: 'border-blue-100', selected: 'border-blue-300 bg-blue-50/50 shadow-md', footerIcon: 'text-blue-600',
    },
    amber: {
        badge: 'bg-amber-500', tag: 'bg-amber-50 text-amber-700', title: 'text-amber-700',
        border: 'border-amber-100', selected: 'border-amber-300 bg-amber-50/50 shadow-md', footerIcon: 'text-amber-600',
    },
    slate: {
        badge: 'bg-slate-700', tag: 'bg-slate-100 text-slate-700', title: 'text-slate-700',
        border: 'border-slate-200', selected: 'border-slate-400 bg-slate-50/70 shadow-md', footerIcon: 'text-slate-600',
    },
    rose: {
        badge: 'bg-rose-600', tag: 'bg-rose-50 text-rose-700', title: 'text-rose-700',
        border: 'border-rose-100', selected: 'border-rose-300 bg-rose-50/50 shadow-md', footerIcon: 'text-rose-600',
    },
    purple: {
        badge: 'bg-purple-600', tag: 'bg-purple-50 text-purple-700', title: 'text-purple-700',
        border: 'border-purple-100', selected: 'border-purple-300 bg-purple-50/50 shadow-md', footerIcon: 'text-purple-600',
    },
}

// guide - nội dung panel hiện khi khối này được chọn (đúng chủ đề riêng của TỪNG giai đoạn, không
// còn là 1 bộ "tab" tách rời khỏi sơ đồ như bản trước - bấm khối nào, panel đổi theo khối đó).
// guide.type quyết định layout render (xem PermissionFlowSection):
//  - 'columns' (mặc định, block1/2/5): 2 cột trái/phải, mỗi cột 1 list "tiêu đề: mô tả" gọn.
//  - 'stages' (block3 - GX Kiểm Duyệt): 2 thẻ badge màu (Giai đoạn 1/2) + heading + list đánh số -
//    tách theo THỜI ĐIỂM (trước/sau khi duyệt), nhiều việc cần liệt kê rõ thứ tự hơn dạng cột thường.
//  - 'lock' (block4 - Khóa Bí Tích): 3 thẻ giải thích KHI NÀO/TẠI SAO/ẢNH HƯỞNG + dải chip liệt kê
//    ĐỦ 6 bí tích áp dụng cơ chế khoá (lấy từ ALL_SACRAMENT_TYPES/SACRAMENT_LABELS - dữ liệu thật,
//    không gõ tay để khỏi lệch khi hệ thống có thêm/đổi loại bí tích).
const DIAGRAM_BLOCKS = [
    {
        n: 1, color: 'blue', tagKey: 'family overview block1 tag',
        titleKey: 'family overview block1 title', descKey: 'family overview block1 desc',
        footerIcon: <IoPersonOutline size={12}/>, footerKey: 'family overview block1 footer',
        guide: {
            type: 'columns',
            left: {titleKey: 'family overview guide1 left title', subtitleKey: 'family overview guide1 left subtitle',
                items: ['family overview guide1 left item1', 'family overview guide1 left item2', 'family overview guide1 left item3']},
            right: {titleKey: 'family overview guide1 right title', subtitleKey: 'family overview guide1 right subtitle',
                items: ['family overview guide1 right item1', 'family overview guide1 right item2', 'family overview guide1 right item3']},
        },
    },
    {
        n: 2, color: 'amber', tagKey: 'family overview block2 tag',
        titleKey: 'family overview block2 title', descKey: 'family overview block2 desc',
        footerIcon: <IoPaperPlaneOutline size={12}/>, footerKey: 'family overview block2 footer',
        guide: {
            type: 'columns',
            left: {titleKey: 'family overview guide2 left title', subtitleKey: 'family overview guide2 left subtitle',
                items: ['family overview guide2 left item1', 'family overview guide2 left item2', 'family overview guide2 left item3']},
            right: {titleKey: 'family overview guide2 right title', subtitleKey: 'family overview guide2 right subtitle',
                items: ['family overview guide2 right item1', 'family overview guide2 right item2', 'family overview guide2 right item3']},
        },
    },
    {
        n: 3, color: 'slate', tagKey: 'family overview block3 tag',
        titleKey: 'family overview block3 title', descKey: 'family overview block3 desc',
        footerIcon: <IoCheckmarkCircleOutline size={12}/>, footerKey: 'family overview block3 footer',
        guide: {
            type: 'stages',
            stages: [
                {
                    badgeKey: 'family overview guide3 stage1 badge', headingKey: 'family overview guide3 stage1 heading',
                    theme: {badge: 'bg-amber-100 text-amber-800', border: 'border-amber-200'},
                    items: ['family overview guide3 stage1 item1', 'family overview guide3 stage1 item2', 'family overview guide3 stage1 item3'],
                },
                {
                    badgeKey: 'family overview guide3 stage2 badge', headingKey: 'family overview guide3 stage2 heading',
                    theme: {badge: 'bg-emerald-100 text-emerald-800', border: 'border-emerald-200'},
                    items: ['family overview guide3 stage2 item1', 'family overview guide3 stage2 item2'],
                },
            ],
        },
    },
    {
        n: 4, color: 'rose', tagKey: 'family overview block4 tag',
        titleKey: 'family overview block4 title', descKey: 'family overview block4 desc',
        footerIcon: <IoLockClosedOutline size={12}/>, footerKey: 'family overview block4 footer',
        guide: {
            type: 'lock',
            cards: [
                {n: 1, titleKey: 'family overview guide4 card1 title', descKey: 'family overview guide4 card1 desc'},
                {n: 2, titleKey: 'family overview guide4 card2 title', descKey: 'family overview guide4 card2 desc'},
                {n: 3, titleKey: 'family overview guide4 card3 title', descKey: 'family overview guide4 card3 desc'},
            ],
            chipsTitleKey: 'family overview guide4 chips title',
        },
    },
    {
        n: 5, color: 'purple', tagKey: 'family overview block5 tag',
        titleKey: 'family overview block5 title', descKey: 'family overview block5 desc',
        footerIcon: <IoSyncOutline size={12}/>, footerKey: 'family overview block5 footer',
        guide: {
            type: 'columns',
            left: {titleKey: 'family overview guide5 left title', subtitleKey: 'family overview guide5 left subtitle',
                items: ['family overview guide5 left item1', 'family overview guide5 left item2', 'family overview guide5 left item3']},
            right: {titleKey: 'family overview guide5 right title', subtitleKey: 'family overview guide5 right subtitle',
                items: ['family overview guide5 right item1', 'family overview guide5 right item2']},
        },
    },
]

const DiagramBlockCard = ({block, selected, onClick, t}) => {
    const theme = BLOCK_THEMES[block.color]
    return (
        <button
            type="button"
            onClick={onClick}
            className={classNames(
                'p-3 flex flex-col gap-2 text-left background border rounded-xl cursor-pointer transition-all duration-200',
                selected ? theme.selected : classNames(theme.border, 'hover:shadow-sm'),
            )}>
            <div className="flex items-center justify-between gap-2">
                <span className={classNames('w-5 h-5 flex flex-center rounded-full text-white text-2xs font-bold flex-shrink-0', theme.badge)}>
                    {block.n}
                </span>
                <span className={classNames('px-2 py-0.5 rounded-md text-2xs font-bold tracking-wide truncate', theme.tag)}>
                    {t(block.tagKey)}
                </span>
            </div>
            <span className={classNames('font-bold text-sm leading-snug', theme.title)}>{t(block.titleKey)}</span>
            <span className="text-xs text-color-400 leading-snug">{t(block.descKey)}</span>
            <div className="mt-auto pt-2 border-t border-color-50 flex items-center gap-1.5">
                <span className={theme.footerIcon}>{block.footerIcon}</span>
                <span className="text-2xs font-semibold text-color-300">{t(block.footerKey)}</span>
            </div>
        </button>
    )
}

// Nội dung mỗi mục gõ dạng "Tiêu đề ngắn: mô tả" (đúng cách trình bày trong mockup) - tách phần
// trước dấu ':' đầu tiên để in đậm, phần sau hiển thị thường - KHÔNG tách 2 key riêng (title/desc)
// để đỡ nhân đôi số lượng key dịch cho nội dung vốn đã dài. Dùng CHUNG cho cả GuideColumn (list
// thường) lẫn GuideStageCard (list đánh số) - 1 nguồn duy nhất, không chép lại logic tách chuỗi.
const splitHeadDesc = (text) => {
    const sepIdx = text.indexOf(':')
    return {
        head: sepIdx > -1 ? text.slice(0, sepIdx) : null,
        rest: sepIdx > -1 ? text.slice(sepIdx + 1).trim() : text,
    }
}

const GuideColumn = ({col, t}) => (
    <div className="p-3 flex flex-col gap-2.5 background border border-color-50 rounded-xl">
        <div className="flex flex-col min-w-0">
            <span className="font-bold text-sm text-color-000 truncate">{t(col.titleKey)}</span>
            <span className="text-2xs text-color-400 truncate">{t(col.subtitleKey)}</span>
        </div>
        <div className="flex flex-col gap-2">
            {col.items.map((itemKey) => {
                const {head, rest} = splitHeadDesc(t(itemKey))
                return (
                    <div key={itemKey} className="text-xs text-color-400 leading-snug">
                        {head && <span className="font-semibold text-color-000">{head}: </span>}
                        {rest}
                    </div>
                )
            })}
        </div>
    </div>
)

// ─── Layout 'stages' (block3 - GX Kiểm Duyệt): 2 thẻ badge màu theo mốc thời gian ─────────────

const GuideStageCard = ({stage, t}) => (
    <div className={classNames('p-3 flex flex-col gap-2.5 background border rounded-xl', stage.theme.border)}>
        <span className={classNames('w-fit px-2 py-0.5 rounded-md text-2xs font-bold uppercase tracking-wide', stage.theme.badge)}>
            {t(stage.badgeKey)}
        </span>
        <span className="font-bold text-sm text-color-000">{t(stage.headingKey)}</span>
        <div className="flex flex-col gap-2">
            {stage.items.map((itemKey, idx) => {
                const {head, rest} = splitHeadDesc(t(itemKey))
                return (
                    <div key={itemKey} className="flex items-start gap-2 text-xs text-color-400 leading-snug">
                        <span className="w-4 h-4 mt-0.5 flex-shrink-0 flex flex-center rounded-full bg-color-100 text-color-500 text-4xs font-bold">
                            {idx + 1}
                        </span>
                        <span>{head && <span className="font-semibold text-color-000">{head}: </span>}{rest}</span>
                    </div>
                )
            })}
        </div>
    </div>
)

const GuideStagesGrid = ({stages, t}) => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {stages.map((stage) => <GuideStageCard key={stage.badgeKey} stage={stage} t={t}/>)}
    </div>
)

// ─── Layout 'lock' (block4 - Khóa Bí Tích): 3 thẻ giải thích + dải chip 6 bí tích ─────────────

const LockExplainCard = ({card, t}) => (
    <div className="p-3 flex flex-col gap-1.5 background border border-rose-100 rounded-xl">
        <div className="flex items-center gap-1.5">
            <span className="w-5 h-5 flex-shrink-0 flex flex-center rounded-full bg-rose-600 text-white text-2xs font-bold">
                {card.n}
            </span>
            <span className="font-bold text-xs text-rose-700 uppercase tracking-wide">{t(card.titleKey)}</span>
        </div>
        <span className="text-xs text-color-400 leading-snug">{t(card.descKey)}</span>
    </div>
)

const LockMechanismGuide = ({cards, chipsTitleKey, t}) => (
    <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {cards.map((card) => <LockExplainCard key={card.n} card={card} t={t}/>)}
        </div>
        <div className="p-3 flex flex-col gap-2 background border border-color-50 rounded-xl">
            <span className="text-2xs font-bold text-color-400 uppercase tracking-widest">{t(chipsTitleKey)}</span>
            <div className="flex flex-wrap gap-2">
                {ALL_SACRAMENT_TYPES.map((type) => (
                    <span key={type} className="px-2.5 py-1 rounded-full text-2xs font-semibold bg-rose-50 text-rose-700">
                        {t(SACRAMENT_LABELS[type])}
                    </span>
                ))}
            </div>
        </div>
    </div>
)

// Mặc định chọn khối 2 (Gắn Sổ Vào GX) - lý do sổ được liên kết vào giáo xứ là câu hỏi thường gặp
// nhất khi admin mới xem trang này, hợp lý hơn làm điểm vào mặc định so với khối 1 (vận hành độc
// lập, ít cần giải thích thêm).
const PermissionFlowSection = ({t}) => {
    const [activeN, setActiveN] = useState(2)
    const activeBlock = useMemo(() => DIAGRAM_BLOCKS.find((b) => b.n === activeN) || DIAGRAM_BLOCKS[0], [activeN])

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                <div className="flex items-center gap-1.5 text-xs text-color-400">
                    <IoInformationCircleOutline size={14} className="flex-shrink-0"/>
                    {t('family overview diagram hint')}
                </div>
                <span className="text-2xs font-semibold text-color-300 uppercase tracking-wide">
                    {t('family overview diagram stage count')}
                </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                {DIAGRAM_BLOCKS.map((block) => (
                    <DiagramBlockCard key={block.n} block={block} selected={block.n === activeN} onClick={() => setActiveN(block.n)} t={t}/>
                ))}
            </div>
            {activeBlock.guide.type === 'stages' ? (
                <GuideStagesGrid stages={activeBlock.guide.stages} t={t}/>
            ) : activeBlock.guide.type === 'lock' ? (
                <LockMechanismGuide cards={activeBlock.guide.cards} chipsTitleKey={activeBlock.guide.chipsTitleKey} t={t}/>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <GuideColumn col={activeBlock.guide.left} t={t}/>
                    <GuideColumn col={activeBlock.guide.right} t={t}/>
                </div>
            )}
        </div>
    )
}

// ─── Root ───────────────────────────────────────────────────────────────────

const FamilyBookOverviewPage = () => {
    const t = useTranslate()
    const {organization} = useContext(WorkspaceContext)

    return (
        <div className="flex flex-col gap-5">
            <WorkspaceParishOverviewSection organization={organization}/>
            <AccessPortalIntroBlock
                organization={organization}
                featureKey="organization_manage_sacrament"
                titleKey="access portal intro title"
                hintKey="access portal intro hint"
            />
            <PermissionFlowSection t={t}/>
        </div>
    )
}

export default FamilyBookOverviewPage
